export type ReviewPendingPaginationMode = 'combined' | 'heldOnly' | 'heldUploadPendingOnly';

export type ReviewQueueTypeFilter = 'all' | 'posting' | 'removal' | 'held' | 'held_upload_pending';

export type ReviewQueueEmptyState = {
  detail: string;
  showViewSubmissionsAction: boolean;
};

export function isHeldEvidenceBlockedByIncludeReviewed(
  typeFilter: ReviewQueueTypeFilter,
  includeReviewed: boolean,
): boolean {
  return (typeFilter === 'held' || typeFilter === 'held_upload_pending') && includeReviewed;
}

export function describeReviewQueueEmptyMessage(input: {
  typeFilter: ReviewQueueTypeFilter;
  includeReviewed: boolean;
  submissionTotal: number;
  heldTotal: number;
}): ReviewQueueEmptyState {
  if (isHeldEvidenceBlockedByIncludeReviewed(input.typeFilter, input.includeReviewed)) {
    return {
      detail: 'Turn off Include reviewed to view held evidence.',
      showViewSubmissionsAction: false,
    };
  }

  if (input.typeFilter === 'held') {
    if (input.heldTotal === 0 && input.submissionTotal > 0) {
      return {
        detail: 'No held photos are ready for review in this cycle.',
        showViewSubmissionsAction: true,
      };
    }

    return {
      detail: 'No held photos ready for review match the selected filters.',
      showViewSubmissionsAction: false,
    };
  }

  if (input.typeFilter === 'held_upload_pending') {
    return {
      detail: 'No photos are waiting for upload from the operator\'s phone.',
      showViewSubmissionsAction: false,
    };
  }

  if (input.includeReviewed) {
    return {
      detail: 'No reviewed submissions match the selected filters.',
      showViewSubmissionsAction: false,
    };
  }

  return {
    detail: 'No submissions pending review for the selected filters.',
    showViewSubmissionsAction: false,
  };
}

export function describeCombinedTablePaginationNote(input: {
  paginationMode: ReviewPendingPaginationMode;
  reviewPage: number;
  heldTotal: number;
  typeFilter: ReviewQueueTypeFilter;
}): string | undefined {
  if (
    input.paginationMode === 'combined'
    && input.typeFilter === 'all'
    && input.reviewPage === 1
    && input.heldTotal > 0
  ) {
    return `${input.heldTotal} held photo${input.heldTotal === 1 ? '' : 's'} ready for review ${input.heldTotal === 1 ? 'is' : 'are'} pinned on this page and excluded from the submission page count.`;
  }

  return undefined;
}

export type ReviewPendingTotals = {
  submissionTotal: number;
  heldTotal: number;
  heldUploadPendingTotal: number;
  combinedTotal: number;
  paginationTotal: number;
  paginationMode: ReviewPendingPaginationMode;
};

export function buildReviewPendingTotals(input: {
  submissionTotal: number;
  heldTotal: number;
  heldUploadPendingTotal?: number;
  heldOnly: boolean;
  heldUploadPendingOnly?: boolean;
}): ReviewPendingTotals {
  const submissionTotal = Math.max(0, input.submissionTotal);
  const heldTotal = Math.max(0, input.heldTotal);
  const heldUploadPendingTotal = Math.max(0, input.heldUploadPendingTotal ?? 0);
  const combinedTotal = submissionTotal + heldTotal;
  const heldUploadPendingOnly = input.heldUploadPendingOnly === true;

  if (heldUploadPendingOnly) {
    return {
      submissionTotal,
      heldTotal,
      heldUploadPendingTotal,
      combinedTotal,
      paginationTotal: heldUploadPendingTotal,
      paginationMode: 'heldUploadPendingOnly',
    };
  }

  const heldOnly = input.heldOnly;

  return {
    submissionTotal,
    heldTotal,
    heldUploadPendingTotal,
    combinedTotal,
    paginationTotal: heldOnly ? heldTotal : submissionTotal,
    paginationMode: heldOnly ? 'heldOnly' : 'combined',
  };
}

export function resolveReviewPendingPageCount(input: {
  paginationTotal: number;
  pageSize: number;
}): number {
  const pageSize = Math.max(1, input.pageSize);
  return Math.max(1, Math.ceil(Math.max(0, input.paginationTotal) / pageSize));
}

export function shouldIncludeHeldRowsOnPage(input: {
  paginationMode: ReviewPendingPaginationMode;
  reviewPage: number;
}): boolean {
  if (input.paginationMode === 'heldOnly' || input.paginationMode === 'heldUploadPendingOnly') {
    return true;
  }
  return input.reviewPage === 1;
}

export function buildCombinedReviewTableRows<T>(input: {
  paginationMode: ReviewPendingPaginationMode;
  reviewPage: number;
  heldRows: T[];
  submissionRows: T[];
}): T[] {
  if (input.paginationMode === 'heldOnly' || input.paginationMode === 'heldUploadPendingOnly') {
    return input.heldRows;
  }
  return [
    ...(shouldIncludeHeldRowsOnPage(input) ? input.heldRows : []),
    ...input.submissionRows,
  ];
}
